import React, { useState } from 'react';
import { AnalyzeButton } from './AnalyzeButton';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { DisabledTooltip } from './EmptyStateSecondaryButton';
import { DismissAllSuggestionsModal } from './DismissAllSuggestionsModal';
import { RotatingDeleteIcon } from './RotatingDeleteIcon';

type SuggestionsPanelHeaderActionsProps = {
  pendingSuggestionsCount: number;
  onDismissAll?: () => void | Promise<void>;
  isDismissAllDisabled?: boolean;
  dismissAllDisabledTooltip?: string;
  onTriggerAnalysis?: () => void;
  isAnalyzing?: boolean;
  isAnalyzeDisabled?: boolean;
  analyzeTooltip?: string;
  t: (key: string) => string;
};

export const SuggestionsPanelHeaderActions: React.FC<SuggestionsPanelHeaderActionsProps> = ({
  pendingSuggestionsCount,
  onDismissAll,
  isDismissAllDisabled = false,
  dismissAllDisabledTooltip,
  onTriggerAnalysis,
  isAnalyzing = false,
  isAnalyzeDisabled = false,
  analyzeTooltip,
  t,
}) => {
  const [showDismissAllModal, setShowDismissAllModal] = useState(false);
  const [isDismissingAll, setIsDismissingAll] = useState(false);

  const handleConfirmDismissAll = async (): Promise<void> => {
    if (!onDismissAll || isDismissAllDisabled) return;
    setShowDismissAllModal(false);
    setIsDismissingAll(true);
    try {
      await onDismissAll();
    } finally {
      setIsDismissingAll(false);
    }
  };

  return (
    <>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {pendingSuggestionsCount > 0 && onDismissAll && (
          <DisabledTooltip
            enabled={isDismissAllDisabled}
            text={dismissAllDisabledTooltip || t('dismiss_all_suggestions_unavailable_until_saved')}
          >
            <Button
              type="button"
              variant="outline"
              disabled={isDismissAllDisabled}
              onClick={() => setShowDismissAllModal(true)}
              className="group h-8 gap-1"
            >
              <RotatingDeleteIcon />
              {t('dismiss_all_suggestions')}
            </Button>
          </DisabledTooltip>
        )}
        {onTriggerAnalysis &&
          (isAnalyzeDisabled && analyzeTooltip && !isAnalyzing ? (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <AnalyzeButton
                      onClick={onTriggerAnalysis}
                      isAnalyzing={isAnalyzing}
                      disabled={isAnalyzeDisabled}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs border border-border bg-popover text-popover-foreground shadow-md">
                  <p>{analyzeTooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <AnalyzeButton
              onClick={onTriggerAnalysis}
              isAnalyzing={isAnalyzing}
              disabled={isAnalyzeDisabled}
            />
          ))}
      </div>
      {onDismissAll && (
        <DismissAllSuggestionsModal
          open={showDismissAllModal}
          onOpenChange={setShowDismissAllModal}
          onConfirm={handleConfirmDismissAll}
          isConfirming={isDismissingAll}
          t={t}
        />
      )}
    </>
  );
};
